import type { FolderTreeNode } from '@/components/doc-control/folder-tree'
import type { PhotoFolder } from '../types'

export const ALL_PHOTOS = '__all__'
const MAX_DEPTH = 6

export type FolderLabels = { inbox: string; snags: string; meetings: string }

export const folderLabels = (t: (k: string) => string): FolderLabels => ({ inbox: t('Photos_inbox'), snags: t('Photos_folder_snags'), meetings: t('Photos_folder_meetings') })

export const folderLabel = (f: Pick<PhotoFolder, 'isInbox' | 'system' | 'name'>, labels: FolderLabels): string =>
  f.isInbox ? labels.inbox : f.system === 'SNAGS' ? labels.snags : f.system === 'MEETINGS' ? labels.meetings : f.name

const rank = (f: PhotoFolder) => (f.isInbox ? 0 : f.system === 'SNAGS' ? 1 : f.system === 'MEETINGS' ? 2 : 3)

export const bySort = (a: PhotoFolder, b: PhotoFolder) =>
  rank(a) - rank(b) || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)

const childrenOf = (folders: PhotoFolder[], parentId: string | null) => folders.filter((x) => x.parentId === parentId).sort(bySort)

export function buildFolderTree(folders: PhotoFolder[], labels: FolderLabels): FolderTreeNode[] {
  const build = (parentId: string | null): FolderTreeNode[] =>
    childrenOf(folders, parentId).map((x) => ({
      id: x.id,
      name: folderLabel(x, labels),
      documentCount: x.photoCount,
      children: build(x.id),
    }))
  return build(null)
}

/** Root → `id`; empty when `id` is not a folder (e.g. ALL_PHOTOS). */
export function folderBreadcrumb(folders: PhotoFolder[], id: string): PhotoFolder[] {
  const path: PhotoFolder[] = []
  let cur = folders.find((x) => x.id === id)
  while (cur && path.length <= folders.length) {
    path.unshift(cur)
    const parentId = cur.parentId
    cur = parentId ? folders.find((x) => x.id === parentId) : undefined
  }
  return path
}

const depthOf = (folders: PhotoFolder[], id: string) => folderBreadcrumb(folders, id).length

const heightOf = (folders: PhotoFolder[], id: string): number =>
  1 + childrenOf(folders, id).reduce((max, c) => Math.max(max, heightOf(folders, c.id)), 0)

/** Valid parents for `movingId`: not itself, its subtree or the inbox, and shallow enough for the whole subtree. */
export function moveTargets(folders: PhotoFolder[], movingId: string): PhotoFolder[] {
  const height = heightOf(folders, movingId)
  return folders.filter(
    (x) =>
      !x.isInbox &&
      folderBreadcrumb(folders, x.id).every((a) => a.id !== movingId) &&
      depthOf(folders, x.id) + height <= MAX_DEPTH,
  )
}

export function readDraggedPhotoIds(e: { dataTransfer: { getData(t: string): string } }): string[] {
  try {
    const ids = JSON.parse(e.dataTransfer.getData('application/x-urkla-photos'))
    return Array.isArray(ids) && ids.every((i) => typeof i === 'string') ? ids : []
  } catch (err) {
    return []
  }
}

/** Swaps `id` with its displayed neighbour and returns the siblings whose sortOrder must become their new index. */
export function reorderSiblings(siblings: PhotoFolder[], id: string, direction: -1 | 1): { id: string; sortOrder: number }[] {
  const order = siblings.slice().sort(bySort)
  const from = order.findIndex((x) => x.id === id)
  const to = from + direction
  if (from < 0 || to < 0 || to >= order.length) return []
  const moved = order[from]
  order[from] = order[to]
  order[to] = moved
  return order.map((x, sortOrder) => ({ x, sortOrder })).filter(({ x, sortOrder }) => x.sortOrder !== sortOrder).map(({ x, sortOrder }) => ({ id: x.id, sortOrder }))
}
